import React, { useRef, useState } from "react";

export type ErpOperationServiceId = "DESKTOP_AGENT" | "CMS" | "ERP";
export type ErpOperationHealth = "HEALTHY" | "DEGRADED" | "OFFLINE";

export type ErpOperationService = {
  id: ErpOperationServiceId;
  label: string;
  health: ErpOperationHealth;
  heartbeatAt: string;
  heartbeatAge: string;
  version: string;
  connectionLabel: string;
  recoveryEnabled: boolean;
};

export type ErpSchedulerHistoryItem = {
  id: string;
  occurredAt: string;
  job: string;
  target: string;
  result: "SUCCESS" | "SKIPPED" | "DRY_RUN";
  detail: string;
};

export type ErpCredentialMetadata = {
  id: string;
  label: string;
  maskedReference: string;
  owner: string;
  rotatedAt: string;
  expiresAt: string;
  status: "ACTIVE" | "ROTATION_DUE";
  storageLocation: "AX PC Local Secure Store";
  loginTestEnabled: boolean;
};

export type ErpReportRecipient = {
  id: string;
  label: string;
  email: string;
};

export type ErpOperationsSnapshot = {
  capturedAt: string;
  mode: "DRY_RUN" | "LIVE" | "DISABLED";
  autoSyncEnabled?: boolean;
  overallRecoveryEnabled?: boolean;
  autoSyncIntervalMinutes: 10;
  dailyRestartEnabled: boolean;
  dailyRestartTime: string;
  dailyReportEnabled: boolean;
  dailyReportTime: string;
  services: ErpOperationService[];
  schedulerHistory: ErpSchedulerHistoryItem[];
  credentialMetadata?: ErpCredentialMetadata[];
  incidentRecipients?: ErpReportRecipient[];
  dailyReportRecipients?: ErpReportRecipient[];
  incidentPreview?: string;
  dailyReportPreview?: string;
};

type Props = {
  snapshot: ErpOperationsSnapshot;
  interactivePreview?: boolean;
};

const HEALTH_LABELS: Record<ErpOperationHealth, string> = {
  HEALTHY: "정상",
  DEGRADED: "주의",
  OFFLINE: "연결 끊김",
};

const HEALTH_STYLES: Record<ErpOperationHealth, string> = {
  HEALTHY: "bg-emerald-100 text-emerald-900",
  DEGRADED: "bg-amber-100 text-amber-950",
  OFFLINE: "bg-red-100 text-red-950",
};

const HISTORY_STYLES: Record<ErpSchedulerHistoryItem["result"], string> = {
  SUCCESS: "bg-emerald-100 text-emerald-900",
  SKIPPED: "bg-slate-200 text-slate-800",
  DRY_RUN: "bg-blue-100 text-blue-900",
};

const DEFAULT_SNAPSHOT: ErpOperationsSnapshot = {
  capturedAt: "2026-08-13 09:42 KST",
  mode: "DRY_RUN",
  autoSyncEnabled: true,
  overallRecoveryEnabled: true,
  autoSyncIntervalMinutes: 10,
  dailyRestartEnabled: false,
  dailyRestartTime: "03:30",
  dailyReportEnabled: false,
  dailyReportTime: "20:00",
  services: [
    {
      id: "DESKTOP_AGENT",
      label: "Desktop Agent",
      health: "HEALTHY",
      heartbeatAt: "2026-08-13 09:41 KST",
      heartbeatAge: "1분 전",
      version: "0.2.0-dry-run",
      connectionLabel: "QA-PC · synthetic heartbeat",
      recoveryEnabled: true,
    },
    {
      id: "CMS",
      label: "CMS",
      health: "HEALTHY",
      heartbeatAt: "2026-08-13 09:40 KST",
      heartbeatAge: "2분 전",
      version: "admin-ui synthetic",
      connectionLabel: "local state · API 연결 없음",
      recoveryEnabled: true,
    },
    {
      id: "ERP",
      label: "천년경영 ERP",
      health: "DEGRADED",
      heartbeatAt: "미수집",
      heartbeatAge: "현장 연결 전",
      version: "UIA contract pending",
      connectionLabel: "DRY_RUN · import 차단",
      recoveryEnabled: false,
    },
  ],
  schedulerHistory: [
    {
      id: "schedule-003",
      occurredAt: "2026-08-13 09:40 KST",
      job: "10분 자동 동기화",
      target: "전체 서비스",
      result: "DRY_RUN",
      detail: "synthetic heartbeat 비교만 수행 · 외부 호출 0건",
    },
    {
      id: "schedule-002",
      occurredAt: "2026-08-13 03:30 KST",
      job: "일일 재시작",
      target: "Desktop Agent",
      result: "SKIPPED",
      detail: "DRY_RUN 정책으로 프로세스 제어 생략",
    },
    {
      id: "schedule-001",
      occurredAt: "2026-08-12 18:00 KST",
      job: "일일 운영 보고",
      target: "수신자 미리보기",
      result: "DRY_RUN",
      detail: "메일 본문 생성만 확인 · 발송하지 않음",
    },
  ],
  credentialMetadata: [
    {
      id: "agent-token",
      label: "Desktop Agent 자격 증명",
      maskedReference: "agent-••••-7A2C",
      owner: "전산·AX 운영",
      rotatedAt: "2026-08-01",
      expiresAt: "2026-09-01",
      status: "ACTIVE",
      storageLocation: "AX PC Local Secure Store",
      loginTestEnabled: true,
    },
    {
      id: "cms-service",
      label: "CMS 서비스 계정",
      maskedReference: "cms-••••-19F0",
      owner: "관리자 시스템",
      rotatedAt: "2026-07-15",
      expiresAt: "2026-10-15",
      status: "ACTIVE",
      storageLocation: "AX PC Local Secure Store",
      loginTestEnabled: true,
    },
    {
      id: "erp-profile",
      label: "ERP 실행 프로필",
      maskedReference: "erp-••••-DRY0",
      owner: "ERP 담당자",
      rotatedAt: "미발급",
      expiresAt: "LIVE 승인 전",
      status: "ROTATION_DUE",
      storageLocation: "AX PC Local Secure Store",
      loginTestEnabled: false,
    },
  ],
  incidentRecipients: [
    { id: "incident-ax", label: "전산·AX 담당", email: "ax-ops@example.invalid" },
    { id: "incident-erp", label: "ERP 담당", email: "erp-owner@example.invalid" },
  ],
  dailyReportRecipients: [
    { id: "report-manager", label: "운영 관리자", email: "ops-report@example.invalid" },
    { id: "report-audit", label: "감사 확인", email: "audit-preview@example.invalid" },
  ],
};

const ALREADY_MASKED_REFERENCE = /^[A-Za-z0-9_-]{1,24}-••••-[A-Za-z0-9_-]{1,8}$/;

export const safelyMaskReference = (value: string) =>
  ALREADY_MASKED_REFERENCE.test(value) ? value : "credential:••••:미확인";

const getOverallHealth = (services: ErpOperationService[]) => {
  if (services.some((service) => service.health === "OFFLINE")) return "OFFLINE";
  if (services.some((service) => service.health === "DEGRADED")) return "DEGRADED";
  return "HEALTHY";
};

const ErpOperationsPanel = ({ snapshot, interactivePreview = false }: Props) => {
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(snapshot.autoSyncEnabled === true);
  const [overallRecoveryEnabled, setOverallRecoveryEnabled] = useState(
    snapshot.overallRecoveryEnabled === true
  );
  const [dailyRestartEnabled, setDailyRestartEnabled] = useState(snapshot.dailyRestartEnabled);
  const [dailyRestartTime, setDailyRestartTime] = useState(snapshot.dailyRestartTime);
  const [dailyReportEnabled, setDailyReportEnabled] = useState(snapshot.dailyReportEnabled);
  const [dailyReportTime, setDailyReportTime] = useState(snapshot.dailyReportTime);
  const [recoveryEnabled, setRecoveryEnabled] = useState<Record<ErpOperationServiceId, boolean>>(
    () =>
      snapshot.services.reduce(
        (result, service) => ({ ...result, [service.id]: service.recoveryEnabled }),
        { DESKTOP_AGENT: false, CMS: false, ERP: false }
      )
  );
  const [history, setHistory] = useState(snapshot.schedulerHistory);
  const [lastAction, setLastAction] = useState(
    interactivePreview
      ? "아직 시뮬레이션 작업을 실행하지 않았습니다. 실제 서비스에는 영향이 없습니다."
      : "서버에서 수집된 ERP 운영 상태 Snapshot을 읽기 전용으로 표시합니다."
  );
  const [previewType, setPreviewType] = useState<"INCIDENT" | "DAILY">("INCIDENT");
  const actionSequence = useRef(1);

  const overallHealth = getOverallHealth(snapshot.services);
  const credentialMetadata = snapshot.credentialMetadata || [];
  const incidentRecipients = snapshot.incidentRecipients || [];
  const dailyReportRecipients = snapshot.dailyReportRecipients || [];
  const previewRecipients =
    previewType === "INCIDENT" ? incidentRecipients : dailyReportRecipients;
  const previewSubject =
    previewType === "INCIDENT"
      ? `${interactivePreview ? "[DRY_RUN]" : ""}[ERP Bridge] 복구 Incident 보고 미리보기`
      : `${interactivePreview ? "[DRY_RUN]" : ""}[ERP Bridge] 일일 운영 보고 미리보기`;

  const appendSyntheticHistory = (
    job: string,
    target: string,
    detail: string,
    result: ErpSchedulerHistoryItem["result"] = "DRY_RUN"
  ) => {
    const sequence = actionSequence.current;
    actionSequence.current += 1;
    const historyItem: ErpSchedulerHistoryItem = {
      id: `synthetic-${sequence}`,
      occurredAt: `${snapshot.capturedAt} · SIM-${sequence}`,
      job,
      target,
      result,
      detail,
    };
    setHistory((current) => [historyItem, ...current].slice(0, 8));
    setLastAction(`${target} ${job} 완료: ${detail}`);
  };

  const simulateStatusCheck = (service?: ErpOperationService) => {
    appendSyntheticHistory(
      "상태 점검 시뮬레이션",
      service?.label || "전체 서비스",
      "화면의 synthetic snapshot만 다시 평가 · 네트워크 호출 0건"
    );
  };

  const simulateRestart = (service: ErpOperationService) => {
    const enabled = overallRecoveryEnabled && recoveryEnabled[service.id];
    appendSyntheticHistory(
      "재시작 시뮬레이션",
      service.label,
      enabled
        ? "DRY_RUN 재시작 계획만 기록 · 프로세스 종료/실행 없음"
        : overallRecoveryEnabled
          ? "개별 복구가 꺼져 있어 실행 계획 생성 생략"
          : "전체 자동복구가 꺼져 있어 실행 계획 생성 생략",
      enabled ? "DRY_RUN" : "SKIPPED"
    );
  };

  return (
    <section className="space-y-4" aria-labelledby="erp-operations-title">
      <div className="border border-slate-200 bg-slate-950 p-5 text-white shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-wide text-cyan-300">
              {interactivePreview
                ? "Resilience Supervisor · Synthetic QA Preview"
                : "Resilience Supervisor · Server Runtime Snapshot"}
            </p>
            <h4 id="erp-operations-title" className="mt-1 text-lg font-black">
              ERP Bridge 복구 운영
            </h4>
            <p className="mt-2 max-w-3xl text-xs font-bold leading-5 text-slate-300">
              {interactivePreview
                ? "heartbeat, scheduler, 복구 설정을 화면에서만 시뮬레이션합니다. 실제 API 호출, 프로세스 종료·재시작, ERP 가져오기, 메일 발송은 수행하지 않습니다."
                : "서버에서 전달된 heartbeat, scheduler, 복구, restart, history, report 상태만 표시합니다. 실행 callback은 연결되지 않았습니다."}
            </p>
          </div>
          {interactivePreview ? <button
            type="button"
            onClick={() => simulateStatusCheck()}
            className="min-h-[42px] border border-cyan-400 bg-cyan-950 px-4 text-xs font-black text-cyan-100 focus:outline-none focus:ring-2 focus:ring-cyan-300"
          >
            전체 상태 점검 시뮬레이션
          </button> : null}
        </div>

        <div className="mt-4 flex flex-wrap gap-2 text-[11px] font-black">
          <span className="bg-blue-100 px-3 py-2 text-blue-950">Mode {snapshot.mode}</span>
          <span className={`px-3 py-2 ${HEALTH_STYLES[overallHealth]}`}>
            운영현황 {HEALTH_LABELS[overallHealth]}
          </span>
          <span className="bg-slate-800 px-3 py-2 text-slate-100">
            Snapshot {snapshot.capturedAt}
          </span>
          <span className="bg-red-100 px-3 py-2 text-red-950">
            {interactivePreview ? "외부 호출·kill·import 0건" : "이 화면 실행 action 없음"}
          </span>
        </div>
        <p className="mt-4 text-xs font-bold text-cyan-100" role="status" aria-live="polite">
          {lastAction}
        </p>
      </div>

      <section className="border border-slate-200 bg-white p-5" aria-labelledby="heartbeat-title">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h5 id="heartbeat-title" className="text-sm font-black text-slate-950">
              서비스 heartbeat
            </h5>
            <p className="mt-1 text-xs font-bold text-slate-600">
              {interactivePreview
                ? "화면에 전달된 synthetic snapshot이며 실시간 연결 상태가 아닙니다."
                : "서버 operations payload의 마지막 수집 상태입니다."}
            </p>
          </div>
          <p className="text-xs font-black text-slate-600">
            자동 동기화 주기 {snapshot.autoSyncIntervalMinutes}분
          </p>
        </div>

        <label className="mt-4 flex min-h-[52px] items-center justify-between gap-4 border border-blue-200 bg-blue-50 px-4 text-xs font-black text-blue-950">
          <span>
            전체 자동복구 {interactivePreview ? "시뮬레이션" : "설정"}
            <span className="mt-1 block text-[11px] font-bold text-blue-700">
              OFF이면 Desktop Agent·CMS·ERP 개별 복구와 재시작 계획을 모두 차단
            </span>
          </span>
          <input
            type="checkbox"
            role="switch"
            checked={overallRecoveryEnabled}
            disabled={!interactivePreview}
            onChange={(event) => setOverallRecoveryEnabled(event.target.checked)}
            aria-label="전체 자동복구 시뮬레이션"
            className="h-5 w-5 accent-blue-700"
          />
        </label>

        <div className="mt-4 grid gap-3 lg:grid-cols-3">
          {snapshot.services.map((service) => (
            <article key={service.id} className="border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h6 className="text-sm font-black text-slate-950">{service.label}</h6>
                  <p className="mt-1 text-[11px] font-bold text-slate-500">{service.version}</p>
                </div>
                <span className={`px-2 py-1 text-[11px] font-black ${HEALTH_STYLES[service.health]}`}>
                  {HEALTH_LABELS[service.health]}
                </span>
              </div>
              <dl className="mt-4 space-y-2 text-xs">
                <div className="flex justify-between gap-3">
                  <dt className="font-bold text-slate-500">마지막 heartbeat</dt>
                  <dd className="text-right font-black text-slate-800">{service.heartbeatAt}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="font-bold text-slate-500">경과</dt>
                  <dd className="text-right font-black text-slate-800">{service.heartbeatAge}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="font-bold text-slate-500">연결</dt>
                  <dd className="max-w-[210px] text-right font-black text-slate-800">
                    {service.connectionLabel}
                  </dd>
                </div>
              </dl>

              <label className="mt-4 flex min-h-[42px] items-center justify-between gap-3 border border-slate-200 bg-white px-3 text-xs font-black text-slate-800">
                개별 자동 복구 {interactivePreview ? "시뮬레이션" : "설정"}
                <input
                  type="checkbox"
                  role="switch"
                  checked={recoveryEnabled[service.id]}
                  disabled={!interactivePreview || !overallRecoveryEnabled}
                  onChange={(event) =>
                    setRecoveryEnabled((current) => ({
                      ...current,
                      [service.id]: event.target.checked,
                    }))
                  }
                  aria-label={`${service.label} 자동 복구 시뮬레이션`}
                  className="h-4 w-4 accent-blue-700 disabled:opacity-40"
                />
              </label>

              {interactivePreview ? <div className="mt-3 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => simulateStatusCheck(service)}
                  className="min-h-[42px] border border-slate-300 bg-white px-2 text-xs font-black text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-600"
                >
                  상태 점검
                </button>
                <button
                  type="button"
                  onClick={() => simulateRestart(service)}
                  disabled={!overallRecoveryEnabled}
                  className="min-h-[42px] border border-blue-300 bg-blue-50 px-2 text-xs font-black text-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-600 disabled:border-slate-200 disabled:bg-slate-100 disabled:text-slate-500"
                >
                  재시작 시뮬레이션
                </button>
              </div> : null}
            </article>
          ))}
        </div>
      </section>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
        <section className="border border-slate-200 bg-white p-5" aria-labelledby="scheduler-settings-title">
          <h5 id="scheduler-settings-title" className="text-sm font-black text-slate-950">
            Scheduler 설정
          </h5>
          <p className="mt-1 text-xs font-bold text-slate-600">
            {interactivePreview
              ? "local state에만 반영되며 브라우저 저장소에도 남기지 않습니다."
              : "서버 operations 설정을 읽기 전용으로 표시합니다."}
          </p>

          <div className="mt-4 space-y-3">
            <label className="flex min-h-[48px] items-center justify-between gap-3 border border-slate-200 bg-slate-50 px-3 text-xs font-black text-slate-800">
              <span>
                10분 자동 동기화
                <span className="mt-1 block text-[11px] font-bold text-slate-500">
                  {interactivePreview ? "synthetic heartbeat 비교만 수행" : "서버 설정값"}
                </span>
              </span>
              <input
                type="checkbox"
                role="switch"
                checked={autoSyncEnabled}
                disabled={!interactivePreview}
                onChange={(event) => setAutoSyncEnabled(event.target.checked)}
                aria-label="10분 자동 동기화 시뮬레이션"
                className="h-4 w-4 accent-blue-700"
              />
            </label>

            <label className="flex min-h-[48px] items-center justify-between gap-3 border border-slate-200 bg-slate-50 px-3 text-xs font-black text-slate-800">
              <span>
                일일 자동 재시작
                <span className="mt-1 block text-[11px] font-bold text-slate-500">
                  {interactivePreview ? "DRY_RUN 계획만 허용" : "서버 설정값"}
                </span>
              </span>
              <input
                type="checkbox"
                role="switch"
                checked={dailyRestartEnabled}
                disabled={!interactivePreview}
                onChange={(event) => setDailyRestartEnabled(event.target.checked)}
                aria-label="일일 자동 재시작 시뮬레이션"
                className="h-4 w-4 accent-blue-700"
              />
            </label>

            <label className="block border border-slate-200 bg-slate-50 px-3 py-3 text-xs font-black text-slate-800">
              일일 재시작 시각
              <input
                type="time"
                value={dailyRestartTime}
                disabled={!interactivePreview}
                onChange={(event) => setDailyRestartTime(event.target.value)}
                aria-describedby="restart-time-help"
                className="mt-2 min-h-[42px] w-full border border-slate-300 bg-white px-3 font-black text-slate-900"
              />
              <span id="restart-time-help" className="mt-2 block text-[11px] font-bold text-slate-500">
                {interactivePreview ? "DRY_RUN 예약만 표시 · 실제 OS Scheduler 등록 없음" : "서버 restart 설정의 읽기 전용 값"}
              </span>
            </label>

            {interactivePreview ? <button
              type="button"
              onClick={() =>
                appendSyntheticHistory(
                  "일일 재시작 계획 시뮬레이션",
                  "활성화된 복구 대상",
                  dailyRestartEnabled
                    ? `${dailyRestartTime} 계획만 기록 · 실제 프로세스 제어 없음`
                    : "일일 자동 재시작이 꺼져 있어 계획 생성 생략",
                  dailyRestartEnabled ? "DRY_RUN" : "SKIPPED"
                )
              }
              className="min-h-[42px] w-full border border-blue-300 bg-blue-50 px-3 text-xs font-black text-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-600"
            >
              일일 재시작 계획 미리보기
            </button> : null}
          </div>
        </section>

        <section className="border border-slate-200 bg-white" aria-labelledby="scheduler-history-title">
          <div className="border-b border-slate-200 p-5">
            <h5 id="scheduler-history-title" className="text-sm font-black text-slate-950">
              Scheduler history
            </h5>
            <p className="mt-1 text-xs font-bold text-slate-600">
              {interactivePreview
                ? "최근 synthetic 실행 기록만 표시합니다. Audit 또는 운영 실행 증빙이 아닙니다."
                : "서버 operations payload가 제공한 최근 실행 이력입니다."}
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] border-collapse text-left text-xs">
              <thead className="bg-slate-100 text-slate-700">
                <tr>
                  {["시각", "작업", "대상", "결과", "상세"].map((header) => (
                    <th key={header} scope="col" className="border-b border-slate-200 px-3 py-3 font-black">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {history.map((item) => (
                  <tr key={item.id} className="border-b border-slate-100">
                    <td className="whitespace-nowrap px-3 py-3 font-bold text-slate-700">{item.occurredAt}</td>
                    <td className="px-3 py-3 font-black text-slate-900">{item.job}</td>
                    <td className="px-3 py-3 font-bold text-slate-700">{item.target}</td>
                    <td className="px-3 py-3">
                      <span className={`px-2 py-1 text-[11px] font-black ${HISTORY_STYLES[item.result]}`}>
                        {item.result}
                      </span>
                    </td>
                    <td className="px-3 py-3 font-bold text-slate-600">{item.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <section className="border border-slate-200 bg-white p-5" aria-labelledby="credential-title">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h5 id="credential-title" className="text-sm font-black text-slate-950">
              자격 증명 metadata
            </h5>
            <p className="mt-1 text-xs font-bold text-slate-600">
              마스킹된 참조·소유자·회전일만 표시합니다. 비밀번호, token 원문, 입력 필드는 없습니다.
            </p>
          </div>
          <span className="bg-slate-100 px-3 py-2 text-[11px] font-black text-slate-700">
            localStorage 저장 없음
          </span>
        </div>
        <div className="mt-4 grid gap-3 lg:grid-cols-3">
          {credentialMetadata.map((credential) => (
            <article key={credential.id} className="border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-start justify-between gap-2">
                <h6 className="text-xs font-black text-slate-950">{credential.label}</h6>
                <span
                  className={`px-2 py-1 text-[10px] font-black ${
                    credential.status === "ACTIVE"
                      ? "bg-emerald-100 text-emerald-900"
                      : "bg-amber-100 text-amber-950"
                  }`}
                >
                  {credential.status}
                </span>
              </div>
              <code className="mt-3 block break-all bg-slate-900 px-3 py-2 text-xs font-black text-cyan-200">
                {safelyMaskReference(credential.maskedReference)}
              </code>
              <dl className="mt-3 space-y-2 text-xs">
                <div className="flex justify-between gap-3">
                  <dt className="font-bold text-slate-500">소유자</dt>
                  <dd className="text-right font-black text-slate-800">{credential.owner}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="font-bold text-slate-500">최근 회전</dt>
                  <dd className="text-right font-black text-slate-800">{credential.rotatedAt}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="font-bold text-slate-500">만료/검토</dt>
                  <dd className="text-right font-black text-slate-800">{credential.expiresAt}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="font-bold text-slate-500">보관 위치</dt>
                  <dd className="text-right font-black text-slate-800">
                    {credential.storageLocation}
                  </dd>
                </div>
              </dl>
              {interactivePreview ? <button
                type="button"
                onClick={() =>
                  appendSyntheticHistory(
                    "로그인 테스트 시뮬레이션",
                    credential.label,
                    "masked metadata 상태만 확인 · credential/API 사용 없음"
                  )
                }
                disabled={!credential.loginTestEnabled}
                className="mt-3 min-h-[42px] w-full border border-slate-300 bg-white px-3 text-xs font-black text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-600 disabled:bg-slate-100 disabled:text-slate-500"
              >
                {credential.loginTestEnabled
                  ? "로그인 테스트 시뮬레이션"
                  : "로그인 테스트 미사용 · 미발급"}
              </button> : null}
            </article>
          ))}
        </div>
      </section>

      <section className="border border-slate-200 bg-white p-5" aria-labelledby="report-preview-title">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h5 id="report-preview-title" className="text-sm font-black text-slate-950">
              Incident·운영 보고 미리보기
            </h5>
            <p className="mt-1 text-xs font-bold text-slate-600">
              {interactivePreview
                ? "synthetic 수신자와 본문만 확인합니다. 발송 action은 제공하지 않습니다."
                : "서버가 제공한 수신자와 보고 Preview만 표시합니다. 메일 발송 action은 없습니다."}
            </p>
          </div>
          <div className="flex gap-2" role="group" aria-label="메일 미리보기 유형">
            <button
              type="button"
              onClick={() => setPreviewType("INCIDENT")}
              aria-pressed={previewType === "INCIDENT"}
              className={`min-h-[42px] border px-3 text-xs font-black focus:outline-none focus:ring-2 focus:ring-blue-600 ${
                previewType === "INCIDENT"
                  ? "border-red-300 bg-red-50 text-red-900"
                  : "border-slate-300 bg-white text-slate-700"
              }`}
            >
              Incident 보고
            </button>
            <button
              type="button"
              onClick={() => setPreviewType("DAILY")}
              aria-pressed={previewType === "DAILY"}
              className={`min-h-[42px] border px-3 text-xs font-black focus:outline-none focus:ring-2 focus:ring-blue-600 ${
                previewType === "DAILY"
                  ? "border-blue-300 bg-blue-50 text-blue-900"
                  : "border-slate-300 bg-white text-slate-700"
              }`}
            >
              일일 운영 보고
            </button>
          </div>
        </div>

        <div className="mt-4 grid gap-3 border border-slate-200 bg-slate-50 p-3 sm:grid-cols-[minmax(0,1fr)_150px]">
          <label className="flex min-h-[42px] items-center justify-between gap-3 text-xs font-black text-slate-800">
            <span>일일 ERP 리포트 {interactivePreview ? "DRY_RUN" : "설정"}</span>
            <input
              type="checkbox"
              role="switch"
              checked={dailyReportEnabled}
              disabled={!interactivePreview}
              onChange={(event) => setDailyReportEnabled(event.target.checked)}
              aria-label="일일 ERP 리포트 시뮬레이션"
              className="h-4 w-4 accent-blue-700"
            />
          </label>
          <label className="text-xs font-black text-slate-800">
            발송 예정 시각
            <input
              type="time"
              value={dailyReportTime}
              disabled={!interactivePreview}
              onChange={(event) => setDailyReportTime(event.target.value)}
              className="mt-1 min-h-[38px] w-full border border-slate-300 bg-white px-2"
            />
          </label>
        </div>

        <div className="mt-4 grid gap-4 lg:grid-cols-[320px_minmax(0,1fr)]">
          <div className="border border-slate-200 bg-slate-50 p-4">
            <h6 className="text-xs font-black text-slate-950">
              {previewType === "INCIDENT" ? "Incident 수신자" : "일일 보고 수신자"}
            </h6>
            <ul className="mt-3 space-y-2">
              {previewRecipients.map((recipient) => (
                <li key={recipient.id} className="border border-slate-200 bg-white px-3 py-2 text-xs">
                  <strong className="block font-black text-slate-900">{recipient.label}</strong>
                  <span className="mt-1 block break-all font-bold text-slate-600">{recipient.email}</span>
                </li>
              ))}
            </ul>
          </div>

          <article className="border border-blue-200 bg-blue-50 p-4" aria-label="메일 본문 미리보기">
            <p className="text-[11px] font-black uppercase text-blue-700">Email preview · 발송 안 함</p>
            <h6 className="mt-2 text-sm font-black text-slate-950">{previewSubject}</h6>
            <dl className="mt-3 grid gap-2 text-xs sm:grid-cols-2">
              <div className="bg-white px-3 py-2">
                <dt className="font-bold text-slate-500">수신</dt>
                <dd className="mt-1 break-all font-black text-slate-800">
                  {previewRecipients.map((recipient) => recipient.email).join(", ")}
                </dd>
              </div>
              <div className="bg-white px-3 py-2">
                <dt className="font-bold text-slate-500">상태 기준</dt>
                <dd className="mt-1 font-black text-slate-800">
                  {snapshot.capturedAt} · {HEALTH_LABELS[overallHealth]}
                </dd>
              </div>
            </dl>
            <div className="mt-3 whitespace-pre-line bg-white px-3 py-3 text-xs font-bold leading-5 text-slate-700">
              {interactivePreview
                ? previewType === "INCIDENT"
                  ? `ERP Bridge synthetic 상태 점검에서 ${HEALTH_LABELS[overallHealth]} 상태가 표시되었습니다.\n실제 장애 판정이나 복구 명령은 실행되지 않았습니다.\n담당자가 운영 source와 ERP를 별도로 확인해야 합니다.`
                  : `자동 동기화: ${autoSyncEnabled ? "10분 DRY_RUN 활성" : "비활성"}\n일일 재시작 계획: ${dailyRestartEnabled ? `${dailyRestartTime} DRY_RUN` : "비활성"}\n일일 ERP 리포트: ${dailyReportEnabled ? `${dailyReportTime} preview` : "비활성"}\n실제 프로세스 제어·ERP import·메일 발송: 0건`
                : previewType === "INCIDENT"
                  ? snapshot.incidentPreview || "Incident report preview 미연결"
                  : snapshot.dailyReportPreview || "Daily ERP report preview 미연결"}
            </div>
          </article>
        </div>
      </section>
    </section>
  );
};

export default ErpOperationsPanel;
